import pluralize from 'pluralize'

import {
  getStatementStatus,
  getStatusDotClass,
  hasStatementFailed,
  isStatementNotDuplicated,
} from '@/modules/projects/status'
import type { Statement, StatementStatusKey } from '@/modules/projects/stores'
import { pagesKeptUnread, pagesRead, pagesSent } from '../../trial'
import { failedSegmentPageCount } from '../tables/statements/coverage'

// Left-to-right order of the progress bar and legend. Segmenting, recognizing and extracting all
// read as "Extracting" here; the wire statuses stay untouched.
export const PAGE_STATUSES = [
  'notStarted',
  'enqueued',
  'extracting',
  'categorizing',
  'succeeded',
  'failed',
  'keptUnread',
] as const

export type PageStatus = (typeof PAGE_STATUSES)[number]

export type PageStatusCounts = Record<PageStatus, number>

export interface PageTally {
  statementCount: number
  totalPages: number
  statuses: PageStatusCounts
  statementsByStatus: PageStatusCounts
}

const PAGE_STATUS_BY_STATEMENT_STATUS: Partial<
  Record<StatementStatusKey, PageStatus>
> = {
  notStarted: 'notStarted',
  enqueued: 'enqueued',
  segmenting: 'extracting',
  recognizing: 'extracting',
  extracting: 'extracting',
  categorizing: 'categorizing',
  succeeded: 'succeeded',
  failed: 'failed',
}

const emptyStatusCounts = (): PageStatusCounts => ({
  notStarted: 0,
  enqueued: 0,
  extracting: 0,
  categorizing: 0,
  succeeded: 0,
  failed: 0,
  keptUnread: 0,
})

const pageStatusOf = (statement: Statement): PageStatus => {
  const status = getStatementStatus(statement.status)
  return (status && PAGE_STATUS_BY_STATEMENT_STATUS[status]) || 'notStarted'
}

const splitReadPagesByStatus = (
  statement: Statement,
): Partial<PageStatusCounts> => {
  const pages = pagesRead(statement)
  if (!hasStatementFailed(statement)) {
    return { [pageStatusOf(statement)]: pages }
  }

  // Pages outside the failed segments were extracted fine
  const failedPages = Math.min(failedSegmentPageCount(statement), pages)
  return { failed: failedPages, succeeded: pages - failedPages }
}

const splitPagesByStatus = (
  statement: Statement,
): Partial<PageStatusCounts> => ({
  ...splitReadPagesByStatus(statement),
  keptUnread: pagesKeptUnread(statement),
})

const addStatusCounts = (
  totals: PageStatusCounts,
  counts: Partial<PageStatusCounts>,
) => {
  for (const status of PAGE_STATUSES) totals[status] += counts[status] ?? 0
}

export const tallyPagesByStatus = (statements: Statement[]): PageTally => {
  const relevant = statements.filter(isStatementNotDuplicated)
  const statuses = emptyStatusCounts()
  const statementsByStatus = emptyStatusCounts()
  let totalPages = 0

  for (const statement of relevant) {
    totalPages += pagesSent(statement)
    addStatusCounts(statuses, splitPagesByStatus(statement))
    statementsByStatus[pageStatusOf(statement)] += 1
    if (pagesKeptUnread(statement) > 0) statementsByStatus.keptUnread += 1
  }

  return {
    statementCount: relevant.length,
    totalPages,
    statuses,
    statementsByStatus,
  }
}

export const describeStatus = (tally: PageTally, status: PageStatus) => {
  const pages = tally.statuses[status]
  const statements = tally.statementsByStatus[status]
  return `${pages} ${pluralize('page', pages)} across ${statements} ${pluralize('statement', statements)}`
}

// Pages the trial kept unread wait for an upgrade, so they never hold the bar open.
export const isEveryPageSucceeded = ({ totalPages, statuses }: PageTally) =>
  statuses.succeeded + statuses.keptUnread === totalPages

const KEPT_UNREAD_DOT = 'bg-slate-300'

export const pageStatusDotClass = (status: PageStatus) =>
  status === 'keptUnread' ? KEPT_UNREAD_DOT : getStatusDotClass(status)
